import React, { memo } from 'react';
import { Card, CardBody } from './Card';

/**
 * MetricCard - Displays a metric with icon, value, and optional change indicator
 * @param {Object} props
 * @param {string} props.title - Metric title
 * @param {string|number} props.value - Metric value
 * @param {string|number} [props.change] - Change text or percentage
 * @param {boolean} [props.showChangeAsPercentage] - Show change as percentage with +/- indicator
 * @param {React.ComponentType} props.icon - Lucide icon component
 * @param {string} [props.variant] - Color variant (primary, secondary, success, warning, error, info)
 * @param {string} [props.customColor] - Custom color class (e.g., 'bg-purple-500')
 * @param {string} [props.className] - Additional CSS classes
 */
export const MetricCard = memo(function MetricCard({
  title,
  value,
  change,
  showChangeAsPercentage = false,
  icon: Icon,
  variant = 'primary',
  customColor,
  className = ''
}) {
  // Variant to color mapping
  const variantColors = {
    primary: 'bg-primary/10 text-primary',
    secondary: 'bg-secondary/10 text-secondary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    error: 'bg-error/10 text-error',
    info: 'bg-info/10 text-info',
    accent: 'bg-accent/10 text-accent',
    neutral: 'bg-neutral/10 text-neutral'
  };

  const colorClasses = customColor || variantColors[variant] || variantColors.primary;

  // Determine change styling for percentage display
  const isPositiveChange = showChangeAsPercentage && typeof change === 'number' && change >= 0;
  const changeColorClass = showChangeAsPercentage
    ? (isPositiveChange ? 'text-success' : 'text-error')
    : 'text-base-content/50';

  // Format change display
  const formatChange = () => {
    if (!change && change !== 0) return null;
    
    if (showChangeAsPercentage && typeof change === 'number') {
      return `${isPositiveChange ? '+' : ''}${change}% from yesterday`;
    }
    
    return change;
  };

  // Format value display
  const formatValue = () => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  return (
    <Card className={className}>
      <CardBody>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-base-content/60">
              {title}
            </h3>
            <p className="mt-2 text-2xl lg:text-3xl font-bold text-base-content">
              {formatValue()}
            </p>
            {formatChange() && (
              <p className={`mt-1 text-sm ${changeColorClass}`}>
                {formatChange()}
              </p>
            )}
          </div>
          {Icon && (
            <div className={`p-2 lg:p-3 rounded-lg ${colorClasses}`}>
              <Icon className="w-5 h-5 lg:w-6 lg:h-6" />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
});

// Named export for consistency
export default MetricCard;